// NodeTypeMutationService.js

import { functions, httpsCallable } from 'firebaseConfig';

// Cache
import { patchNodeTypeMissionsForWorkspace } from 'services/nodetypes/NodeTypesCacheService';

const callable = (name) => httpsCallable(functions, name);

export async function mutateNodeType(payload = {}) {
  const fn = callable('mutateNodeType');
  const { data } = await fn(payload);
  return data || null;
}

export async function addNodeTypeField({
  nodeKey,
  typeKey,
  workspaceKey,
  field,
  insertBeforeName,
} = {}) {
  if (!typeKey || !field) {
    throw new Error('addNodeTypeField: invalid arguments');
  }

  return mutateNodeType({
    action: 'field.add',
    nodeKey,
    typeKey,
    workspaceKey: workspaceKey || null,
    field,
    insertBeforeName: insertBeforeName || null,
  });
}

export async function updateNodeTypeField({
  nodeKey,
  typeKey,
  workspaceKey,
  field,
  reconcileChecklistPolicy,
} = {}) {
  if (!typeKey || !field) {
    throw new Error('updateNodeTypeField: invalid arguments');
  }

  return mutateNodeType({
    action: 'field.update',
    nodeKey,
    typeKey,
    workspaceKey: workspaceKey || null,
    field,
    reconcileChecklistPolicy: reconcileChecklistPolicy || null,
  });
}

export async function deleteNodeTypeField({
  nodeKey,
  typeKey,
  workspaceKey,
  fieldName,
} = {}) {
  if (!typeKey || !fieldName) {
    throw new Error('deleteNodeTypeField: invalid arguments');
  }

  return mutateNodeType({
    action: 'field.delete',
    nodeKey,
    typeKey,
    workspaceKey: workspaceKey || null,
    fieldName,
  });
}

export async function reorderNodeTypeFields({
  nodeKey,
  typeKey,
  workspaceKey,
  orderedFields,
} = {}) {
  if (!typeKey || !Array.isArray(orderedFields)) {
    throw new Error('reorderNodeTypeFields: invalid arguments');
  }

  return mutateNodeType({
    action: 'field.reorder',
    nodeKey,
    typeKey,
    workspaceKey: workspaceKey || null,
    orderedFields,
  });
}

export async function updateNodeTypeIconKey({ workspaceKey, typeKey, iconKey } = {}) {
  if (!workspaceKey || !typeKey) {
    throw new Error('updateNodeTypeIconKey: invalid arguments');
  }

  return mutateNodeType({
    action: 'icon.update',
    workspaceKey,
    typeKey,
    iconKey: typeof iconKey === 'string' ? iconKey : '',
  });
}

export async function updateNodeTypeMeta({ workspaceKey, typeKey, label, labelPlural, description, iconKey, defaultView } = {}) {
  if (!typeKey) {
    throw new Error('updateNodeTypeMeta: invalid arguments');
  }

  const normalizedLabel = String(label || '').trim();
  const normalizedPlural = String(labelPlural || '').trim();
  const normalizedDescription = String(description || '').trim();
  const normalizedIconKey = String(iconKey || '').trim();
  const normalizedDefaultView = String(defaultView || '').trim().toUpperCase();

  if (!normalizedLabel) {
    throw new Error('updateNodeTypeMeta: label is required');
  }

  const mutationPayload = {
    action: 'type.updateMeta',
    workspaceKey: workspaceKey || null,
    typeKey,
    label: normalizedLabel,
    labelPlural: normalizedPlural || normalizedLabel,
    description: normalizedDescription,
    iconKey: normalizedIconKey,
    defaultView: normalizedDefaultView,
  };

  const isScopeNotFoundError = (error) => {
    const code = String(error?.code || '').toLowerCase();
    const message = String(error?.message || '').toLowerCase();
    return code === 'functions/not-found'
      || code === 'not-found'
      || message.includes('node type not found');
  };

  try {
    return await mutateNodeType(mutationPayload);
  } catch (error) {
    // Root/system types live in the global collection. If the UI passed a workspace
    // key and that scoped doc does not exist, retry against root scope.
    if (!workspaceKey || !isScopeNotFoundError(error)) {
      throw error;
    }

    return mutateNodeType({
      ...mutationPayload,
      workspaceKey: null,
    });
  }
}

export async function updateNodeTypeRollups({ workspaceKey, typeKey, rollups } = {}) {
  if (!typeKey) {
    throw new Error('updateNodeTypeRollups: invalid arguments');
  }

  return mutateNodeType({
    action: 'type.updateRollups',
    workspaceKey: workspaceKey || null,
    typeKey,
    rollups: Array.isArray(rollups) ? rollups : [],
  });
}

export async function updateNodeTypeMissions({ workspaceKey, typeKey, missions } = {}) {
  if (!typeKey) {
    throw new Error('updateNodeTypeMissions: invalid arguments');
  }

  const result = await mutateNodeType({
    action: 'type.updateMissions',
    workspaceKey: workspaceKey || null,
    typeKey,
    missions: missions && typeof missions === 'object' && !Array.isArray(missions)
      ? missions
      : { templates: [] },
  });

  patchNodeTypeMissionsForWorkspace({
    missions: result?.missions,
    typeKey,
    workspaceKey,
  });

  return result;
}

export async function deleteNodeType({ workspaceKey, typeKey, deleteNodes = false } = {}) {
  if (!workspaceKey || !typeKey) {
    throw new Error('deleteNodeType: invalid arguments');
  }

  return mutateNodeType({
    action: 'type.delete',
    workspaceKey,
    typeKey,
    deleteNodes: deleteNodes === true,
  });
}

export async function purgeNodeTypeWorkspaceData({ workspaceKey, typeKey, deleteNodes = false } = {}) {
  if (!workspaceKey || !typeKey) {
    throw new Error('purgeNodeTypeWorkspaceData: invalid arguments');
  }

  return mutateNodeType({
    action: 'type.purgeWorkspaceData',
    workspaceKey,
    typeKey,
    deleteNodes: deleteNodes === true,
  });
}
